import * as React from 'react';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { cssClass } from '../../style/index';
import { FlowRunErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { FlowRunError } from '../../ts/ServerApi/types/FlowRunError';
import { HelpService } from '../../ts/services/HelpService';

const helpUrl = cssClass('flowMessage', 'helpUrl', {
  paddingLeft: CommonStyles.Sizes.SmallSpace
});

export function flowRunErrorMessageFor(error: FlowRunError) {
  switch (error.flowRunErrorCode) {
    case FlowRunErrorCode.FlowRunUnknownError:
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunLoomSystemError:
      // This error is intentionally listed as unknown: there isn't sufficient information for a more detailed message.
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunNodeExecutionError:
      return Messages.errorFlowRunNodeExecution({ STEP_NAME: L10n.elideIfLong(error.errorObjectName)});
    case FlowRunErrorCode.FlowRunMaestroDocumentError:
      // This error is intentionally listed as unknown: there isn't sufficient information for a more detailed message.
      return Messages.errorFlowRunUnknown();
    case FlowRunErrorCode.FlowRunUnsupportedDocumentFeature:
      return Messages.errorFlowRunUnsupportedDocumentFeature();
    case FlowRunErrorCode.FlowRunInputConnectionError:
      return Messages.errorFlowRunInputConnectivity({ CONNECTION_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunOutputError:
      return Messages.errorFlowRunOutputConnectivity({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunPublishingError:
      return Messages.errorFlowRunPublishingOutput({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunInvalidUsernameOrPasswordError:
      return Messages.errorFlowRunInvalidUsernameOrPassword();
    case FlowRunErrorCode.FlowRunInputDriverError:
      return Messages.errorFlowRunInputDriver();
    case FlowRunErrorCode.FlowRunUnapprovedRead:
      return [Messages.errorFlowRunUnapprovedRead(),
        <a key={FlowRunErrorCode.FlowRunUnapprovedRead} className={helpUrl} href={buildHelpUrl('prep-conductor-safelist')}>{Messages.learnMore()}</a>];
    case FlowRunErrorCode.FlowRunUnapprovedWrite:
      return [Messages.errorFlowRunUnapprovedWrite(),
        <a key={FlowRunErrorCode.FlowRunUnapprovedWrite} className={helpUrl} href={buildHelpUrl('prep-conductor-safelist')}>{Messages.learnMore()}</a>];
    case FlowRunErrorCode.FlowRunLoomCompilationError:
      return Messages.errorFlowRunLoomCompilation();
    case FlowRunErrorCode.FlowRunIrrelevantLoomCompiliationError:
      return Messages.errorFlowRunLoomCompilation();
    case FlowRunErrorCode.FlowRunNodeCompilationError:
      return Messages.errorFlowRunNodeCompilation({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunNodeExpressionError:
      return Messages.errorFlowRunNodeExpression({ STEP_NAME: L10n.elideIfLong(error.errorObjectName) });
    case FlowRunErrorCode.FlowRunTimeoutError:
      return Messages.errorFlowRunTimeout();
    case FlowRunErrorCode.FlowRunPublishDatasourcePermissionsError:
      return Messages.errorFlowRunPermission();
    default:
      return Messages.errorFlowRunUnknown();
  }
}

// Currently the only error that can be fixed on server is FlowRunInputConnectionError
const errorsFixableInServer: {[index:number]: boolean} = {[FlowRunErrorCode.FlowRunInputConnectionError] : true};

// Find if there is an error that needs to be fixed in Prep Desktop
export function needsFixInPrepDesktop(errors: ReadonlyArray<FlowRunError>): boolean {
  return errors.find((error) => {
    return !(errorsFixableInServer[error.flowRunErrorCode]);
  }) !== undefined;
}

function buildHelpUrl(helpRedirectToken: string): string {
  const helpEdition: string = 'prep';
  const helpService: HelpService = ServiceLocator.get('HelpService');
  return helpService.getHelpUrlByCategory(helpRedirectToken, helpEdition);
}
